// src/services/api.js
import axios from 'axios';

// Use environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://super-backend.hira-tech.co.uk';

const api = axios.create({
  baseURL: API_BASE_URL, // Base URL without trailing slash
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/user/refresh/`, {
          refresh: refreshToken,
        });
        localStorage.setItem('access_token', response.data.access);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
        originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Profile API calls
export const profileAPI = {
  // Get current user profile
  getProfile: () => api.get('/user/profile/'),

  // Update profile - only send fields that should be updated
  updateProfile: (data) => {
    // Clean the data before sending
    const cleanData = { ...data };

    // Remove address_Id from address object if present
    if (cleanData.address && cleanData.address.address_Id) {
      delete cleanData.address.address_Id;
    }

    // Format phone number if needed
    if (cleanData.phone_Number) {
      let phone = cleanData.phone_Number;
      // Convert Pakistani number to international format if needed
      if (phone.startsWith('03') && phone.length === 11) {
        phone = '+92' + phone.substring(1);
      } else if (phone.startsWith('3') && phone.length === 10) {
        phone = '+92' + phone;
      }
      cleanData.phone_Number = phone;
    }

    return api.put('/user/profile/', cleanData);
  },

  // Get user by ID
  getUserById: (userId) => api.get(`/user/Uprofile/${userId}/`),

  // Update user by ID (admin)
  updateUserById: (userId, data) => api.put(`/user/update/${userId}/`, data),

  // Delete user
  deleteUser: (userId) => api.delete(`/user/delete/${userId}/`),

  // Change password
  changePassword: (data) => api.post('/user/change-password/', data),

  // Get all shops (for dropdown)
  getAllShops: () => api.get('/user/shops/'),

  // Get company addresses
  getCompanyAddresses: (shopId = null) => {
    const url = shopId ? `/user/company-addresses/?shop_id=${shopId}` : '/user/company-addresses/';
    return api.get(url);
  },

  // Create company address
  createCompanyAddress: (data) => api.post('/user/company-addresses/', data),

  // Update company address
  updateCompanyAddress: (addressId, data) => api.put(`/user/company-addresses/${addressId}/`, data),

  // Delete company address
  deleteCompanyAddress: (addressId) => api.delete(`/user/company-addresses/${addressId}/`),
};

// Auth API calls
export const authAPI = {
  login: (email, password) => api.post('/user/login/', { email, password }),
  register: (userData) => api.post('/user/register/', userData),
  refreshToken: (refresh) => api.post('/user/refresh/', { refresh }),
};

// Admin API calls
export const adminAPI = {
  getAllUsers: (shopId = null, userType = null) => {
    let url = '/user/users/';
    const params = new URLSearchParams();
    if (shopId) params.append('shop_id', shopId);
    if (userType) params.append('type', userType);
    if (params.toString()) url += `?${params.toString()}`;
    return api.get(url);
  },
  getUserDetail: (userId) => api.get(`/user/users/${userId}/`),
  createUser: (userData) => api.post('/user/users/', userData),
  updateUser: (userId, userData) => api.put(`/user/users/${userId}/`, userData),
  deleteUser: (userId) => api.delete(`/user/users/${userId}/`),
};

// Export default api
export default api;